import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";

import Paper from "@mui/material/Paper";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useLocation, useNavigate } from "react-router-dom";
import { EnhancedTableHead } from "./TableHeader";

import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { useEffect, useState } from "react";
import { Watch } from "react-loader-spinner";
import { palette } from "../../assets/theme/theme";
import useForm from "../../form/hooks/useForm";
import useSort from "../../hooks/sortHook";
import { genericGetWithFilters } from "../../services/api.service";
import { EntityConfig, PropConfig } from "../../types/EntityConfig";
import { ImportanciaOrcamento } from "../../views/Orcamentos/newOrcamentos/IOrcamento";
import { FiltersComponent } from "./Filters";

function descendingComparator(a: any, b: any, orderBy: string) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator(order: string, orderBy: string) {
	return order === "desc" ? (a: any, b: any) => descendingComparator(a, b, orderBy) : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array: any[], comparator: Function) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a: any, b: any) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el: any) => el[0]);
}

const getBoolIcon = (value: any) => {
	return <>{value ? <CheckCircleIcon className="text-lime-500" /> : <CancelIcon className={"text-red-500"} />}</>;
};

const getStateIcon = (value: any) => {
	const icon = () => {
		switch (value) {
			case "Por iniciar":
				return <RemoveCircleIcon className="text-cyan-500" />;
			case "Em andamento":
				return <PendingIcon className="text-yellow-400" />;
			case "Entregue":
				return <CheckCircleIcon className="text-lime-500" />;
			case "Cancelada":
				return <CancelIcon className="text-red-500" />;
		}
	};

	return (
		<>
			<div className="text-[2em]">{icon()}</div>
		</>
	);
};

const getPedidoOrcamentoStateIcon = (value: any) => {
	const icon = () => {
		switch (value) {
			case "OPEN":
				return <RemoveCircleIcon className="text-cyan-500" />;
			case "PROCESSED":
				return <CheckCircleIcon className="text-lime-500" />;
			case "DISCARDED":
				return <CancelIcon className="text-red-500" />;
		}
	};

	return (
		<>
			<div className="text-[2em]">{icon()}</div>
		</>
	);
};

const getOcorrenciasStateIcon = (value: any) => {
	const icon = () => {
		switch (value) {
			case "Em resolução":
				return <PendingIcon className="text-yellow-400" />;
			case "Resolvida":
				return <CheckCircleIcon className="text-lime-500" />;
			case "Por resolver":
				return <CancelIcon className="text-red-500" />;
		}
	};

	return (
		<>
			<div className="text-[2em]">{icon()}</div>
		</>
	);
};

const getImportanciaOrcamento = (value: ImportanciaOrcamento) => {
	const icon = () => {
		switch (value) {
			case ImportanciaOrcamento.SEM_IMPORTANCIA:
				return <KeyboardDoubleArrowDownIcon className="text-red-500 text-lg" style={{ fontSize: "2.4em" }} />;
			case ImportanciaOrcamento.ALGUMA_IMPORTANCIA:
				return <DragHandleIcon className="text-yellow-400" style={{ fontSize: "2.4em" }} />;
			case ImportanciaOrcamento.MUITA_IMPORTANCIA:
				return <KeyboardDoubleArrowUpIcon className="text-lime-500" style={{ fontSize: "2.4em" }} />;
		}
	};

	return (
		<>
			<div className="flex">{icon()}</div>
		</>
	);
};

type TableInputType = {
	tableConfigs: EntityConfig;
	configMapKey: any;
	entityName: string;
	idProp: string;
	title: string;
	specificRoute?: string;
	allowCopy?: boolean;
	generatePdf?: boolean;
	hidePagination?: boolean;
	isSubTable?: boolean;
	hideFilters?: boolean;
	dataBuildFunction: Function;
};

export default function EnhancedTable({ tableConfigs, configMapKey, entityName, idProp, title, specificRoute, allowCopy, generatePdf, hidePagination, isSubTable, hideFilters, dataBuildFunction }: TableInputType) {
	const [mounted, setMounted] = useState(false);

	const [tableRows, setTableRows] = useState<any>(null);

	// const [page, setPage] = useState(0);
	// const [rowsPerPage, setRowsPerPage] = useState(10);
	const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 10 });
	const [rows, setRows] = useState([]);
	const [itemCount, setItemCount] = useState(0);

	const location = useLocation();
	const currentRoute = location.pathname;
	const navigate = useNavigate();

	const filterConfigs = tableConfigs.filter((tc: PropConfig) => tc.filterable);

	const filterForm = useForm({ formConfigs: filterConfigs, filterMode: true });

	const sortHook = useSort();

	useEffect(() => {
		updateEntity();
	}, [pagination, filterForm.state, sortHook.state]);

	const updateEntity = () => {
		genericGetWithFilters(configMapKey ? `/${configMapKey}` : `${currentRoute}`, { ...filterForm.state, page: pagination.page, take: pagination.rowsPerPage, ...sortHook.state }, dataBuildFunction).then((d) => {
			setRows(d.data);
			setItemCount(d.meta.itemCount);
		});
	};

	function goToCustomRoute(row: any) {
		navigate(`/${configMapKey}/${row.id.value}`, { state: { entityName, idProp, configMapKey, title, specificRoute } });
	}

	function goToDefaultRoute(row: any) {
		navigate(`${currentRoute}/${row.id.value}`, { state: { entityName, idProp, configMapKey, allowCopy, generatePdf, title, specificRoute } });
	}

	function handleRowClick(row: any) {
		configMapKey ? goToCustomRoute(row) : goToDefaultRoute(row);
	}

	useEffect(() => {
		setTableRows(rows);
	}, [rows]);

	const handleRequestSort = (event: any, property: string) => {
		sortHook.update(property);
	};

	const handleChangePage = (event: any, newPage: any) => {
		setPagination({ ...pagination, page: newPage });
	};

	const handleChangeRowsPerPage = (event: any) => {
		setPagination({ ...pagination, rowsPerPage: parseInt(event.target.value, 10) });
	};

	const getNestedValue = (option: any, labelKey: string) => {
		const keys: string[] = labelKey.split(".");
		for (let key of keys) {
			if (!option[key]) return "";
			option = option[key];
		}
		return option;
	};

	const getValueToDisplay = (v: any, tableConfig: any) => {
		if (tableConfig.displayFunction) {
			return tableConfig.displayFunction(v);
		}

		if (tableConfig.type == "boolean") {
			return getBoolIcon(v.value);
		} else if (tableConfig.type == "select") {
			return v?.value ? getNestedValue(v.value, tableConfig.options.label) : null;
		} else if (tableConfig.type == "multi-select" || tableConfig.type === "multiple-create") {
			if (tableConfig.options.deeplyNestedValue) {
				return tableConfig.options.deeplyNestedValue(v);
			}

			let str = "";
			v?.value?.forEach((a: any, idx: number) => {
				str += idx < v.value.length - 1 ? `${a[tableConfig.options.label]}, ` : `${a[tableConfig.options.label]}`;
			});
			return str;
		} else if (tableConfig.type === "enum" && tableConfig.id === "state") {
			return getStateIcon(v.value);
		} else if (tableConfig.type === "enum" && tableConfig.id === "estadoPedidoOrcamento") {
			return getPedidoOrcamentoStateIcon(v.value);
		} else if (tableConfig.type === "enum" && tableConfig.id === "estado") {
			return getOcorrenciasStateIcon(v.value);
		} else if (tableConfig.type === "enum" && tableConfig.id === "importancia") {
			return getImportanciaOrcamento(v.value);
		}

		if (tableConfig.displayInfo) {
			if (tableConfig.displayInfo == "currency") return `${v?.value}€`;
		}

		return v?.value;
	};

	return (
		<Box className="w-full pb-5">
			<div className="my-4">
				<FiltersComponent filterForm={filterForm} filterConfigs={filterConfigs} hideFilters={hideFilters} />
			</div>
			{tableRows ? (
				<Paper sx={{ width: "100%", mb: 2 }}>
					<TableContainer>
						<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
							<EnhancedTableHead headCells={tableConfigs} onRequestSort={handleRequestSort} />
							<TableBody>
								{tableRows.map((row: any, index: number) => {
									return (
										<TableRow onClick={() => handleRowClick(row)} tabIndex={-1} key={index} className={`cursor-pointer bg-white hover:bg-red-200 ${index % 2 == 0 ? "bg-zinc-100" : ""}`}>
											<TableCell />
											{tableConfigs
												.filter((tc: any) => !tc.hidden)
												.filter((tc: PropConfig) => !tc.hideColumn)
												.map((tableConfig: any, idx: number) => {
													const value = row[tableConfig.id];
													return (
														<TableCell padding={"none"} className={"h-14 w-96 max-w-96"} align={"left"} key={idx}>
															{getValueToDisplay(value, tableConfig)}
														</TableCell>
													);
												})}
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
					{hidePagination ? null : <TablePagination className="bg-rodrimaq-red-light" rowsPerPageOptions={[5, 10, 25]} component="div" count={itemCount} rowsPerPage={pagination.rowsPerPage} page={pagination.page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />}
				</Paper>
			) : (
				<div style={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center", justifyContent: "center", height: 300 }}>
					<Watch height="100" width="100" color={palette.rodrimaq_red} ariaLabel="loading" />
					<div style={{ marginTop: 20 }}>
						<span style={{ color: palette.rodrimaq_red, fontSize: "1.4em" }}>A preparar dados...</span>
					</div>
				</div>
			)}
		</Box>
	);
}
